import type { RouteObject } from "react-router-dom";
import { createBrowserRouter, matchRoutes } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { isOnBlogSubdomain } from "./lib/blog-host";
import { NotFoundPage } from "./pages/NotFoundPage";

/**
 * Route table factory with ROUTE-LEVEL CODE-SPLITTING: every page loads
 * through `route.lazy`, so the entry bundle carries only the shell (Layout
 * chrome, router, query client) and each page ships as its own chunk —
 * notably the markdown pipeline (react-markdown/remark/rehype) rides ONLY in
 * the blog-post chunk.
 *
 * Eager exceptions: <Layout> (every page's chrome — lazy would flash it) and
 * <NotFoundPage> (the errorElement must render synchronously mid-error).
 *
 * Resolution contract (the plan-flagged pitfall): `createBrowserRouter`
 * renders a null fallback over an UNRESOLVED lazy match on first render, so
 * `createAppRouter` (below) must only be called after the CURRENT location's
 * lazies are resolved — `resolveMatchedLazies` does that; `main.tsx` awaits
 * it before hydrateRoot/createRoot. Client-side navigations don't need it:
 * the router awaits `lazy()` in place before committing, so there is never a
 * blank flash on nav. The static prerenderer needs nothing either —
 * `createStaticHandler.query()` resolves lazies itself.
 *
 * The one host-dependent decision — whether "/" is the marketing home page
 * or the blog list — stays an explicit argument, not a window read.
 */
export function buildRoutes(onBlogHost: boolean): RouteObject[] {
  return [
    // Chrome-less admin fund PREVIEW surface — iframed by the admin dashboard and
    // driven entirely by postMessage drafts. Deliberately a TOP-LEVEL route
    // (outside <Layout>) so it renders with no site nav/footer. Sits ABOVE the
    // "/" route so it's matched before the catch-all ":slug" fund route.
    {
      path: "/preview/:slug",
      lazy: async () => ({ Component: (await import("./pages/PreviewPage")).PreviewPage }),
    },
    {
      path: "/",
      element: <Layout />,
      errorElement: <NotFoundPage />,
      children: [
        // The SPA is served on BOTH the main host and the blog host
        // (blog.corgifunds.com). On the blog host the root IS the blog, so render
        // the blog list at "/" there instead of the main marketing home page.
        {
          index: true,
          lazy: onBlogHost
            ? async () => ({ Component: (await import("./pages/BlogListPage")).BlogListPage })
            : async () => ({ Component: (await import("./pages/HomePage")).HomePage }),
        },
        {
          path: "products",
          lazy: async () => ({ Component: (await import("./pages/ProductsPage")).ProductsPage }),
        },
        // Category landings — each redirects to /products?category=… (matches main).
        {
          path: "thematic",
          lazy: async () => ({
            Component: (await import("./pages/ThematicProductsPage")).ThematicProductsPage,
          }),
        },
        {
          path: "2xleveraged",
          lazy: async () => ({
            Component: (await import("./pages/LeveragedProductsPage")).LeveragedProductsPage,
          }),
        },
        {
          path: "buffered",
          lazy: async () => ({
            Component: (await import("./pages/BufferProductsPage")).BufferProductsPage,
          }),
        },
        {
          path: "company",
          lazy: async () => ({ Component: (await import("./pages/CompanyPage")).CompanyPage }),
        },
        {
          path: "blog",
          lazy: async () => ({ Component: (await import("./pages/BlogListPage")).BlogListPage }),
        },
        {
          path: "blog/:slug",
          lazy: async () => ({ Component: (await import("./pages/BlogPostPage")).BlogPostPage }),
        },
        {
          path: "glossary",
          lazy: async () => ({ Component: (await import("./pages/GlossaryPage")).GlossaryPage }),
        },
        {
          path: "privacy",
          lazy: async () => ({ Component: (await import("./pages/PrivacyPage")).PrivacyPage }),
        },
        {
          path: "terms",
          lazy: async () => ({ Component: (await import("./pages/TermsPage")).TermsPage }),
        },
        {
          path: "social-media-disclaimer",
          lazy: async () => ({
            Component: (await import("./pages/SocialMediaDisclaimerPage"))
              .SocialMediaDisclaimerPage,
          }),
        },
        // Fund detail canonical route — must stay last so the more specific
        // routes above win when their paths happen to be one segment too.
        {
          path: ":slug",
          lazy: async () => ({
            Component: (await import("./pages/FundDetailPage")).FundDetailPage,
          }),
        },
        { path: "*", element: <NotFoundPage /> },
      ],
    },
  ];
}

type LazyRoute = RouteObject & { lazy?: () => Promise<object> };

/**
 * Resolve `route.lazy` for every route matched by `pathname`, mutating each
 * matched route into its resolved form (`lazy` cleared). Must complete before
 * the router is created for the initial render — see the contract above.
 */
export async function resolveMatchedLazies(routes: RouteObject[], pathname: string): Promise<void> {
  const matches = matchRoutes(routes, pathname) ?? [];
  await Promise.all(
    matches.map(async (m) => {
      const route = m.route as LazyRoute;
      if (!route.lazy) return;
      const mod = await route.lazy();
      Object.assign(route, mod, { lazy: undefined });
    }),
  );
}

/**
 * Warm the remaining route chunks once the browser is idle, so client-side
 * navigations resolve instantly from the module cache. Fire-and-forget;
 * failures are ignored (the router will just fetch on demand as today).
 */
export function idlePrefetchRoutes(routes: RouteObject[]): void {
  // Production-build behavior only. Under the dev server there are no real
  // chunks — each lazy() triggers an on-demand source transform, and a
  // 14-module compile storm per page load starves the shared server (the
  // visual suite flaked exactly this way: pages under test rendered late
  // behind the previous test's prefetch backlog).
  if (import.meta.env.DEV) return;
  const lazies: Array<() => Promise<object>> = [];
  const collect = (list: RouteObject[]) => {
    for (const route of list) {
      const l = (route as LazyRoute).lazy;
      if (l) lazies.push(l);
      if (route.children) collect(route.children);
    }
  };
  collect(routes);
  if (lazies.length === 0) return;

  const warm = () => {
    for (const l of lazies) void l().catch(() => {});
  };
  // Arm the idle prefetch only AFTER the load event. requestIdleCallback fires
  // the moment the main thread first goes quiet, which on an image-heavy page
  // is well BEFORE `load` — a throttled PSI trace caught all 14 route chunks
  // downloading inside the LCP critical path (~1,364-1,417ms), stealing
  // bandwidth from the hero paint this prefetch is supposed to stay clear of.
  // Same fix pattern as the ad-pixel deferral (lib/ad-pixels.ts): gate on load,
  // readyState-aware. Client-side navigations still resolve their own lazies in
  // place, so nav latency is unaffected.
  const scheduleIdle = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(warm, { timeout: 5_000 });
    } else {
      window.setTimeout(warm, 2_000);
    }
  };
  if (document.readyState === "complete") {
    scheduleIdle();
  } else {
    window.addEventListener("load", scheduleIdle, { once: true });
  }
}

/**
 * Browser router for the host the page is being served on. Called from
 * `main.tsx` (never at module load — `createBrowserRouter` touches
 * window.history, which doesn't exist during prerendering) and ONLY after
 * `resolveMatchedLazies` has settled for the current location.
 */
export function createAppRouter(routes: RouteObject[]) {
  return createBrowserRouter(routes);
}
